import { measureAsync, measureSync } from "@scalar/helpers/testing/measure";
import { bundle } from "@scalar/json-magic/bundle";
import { fetchUrls } from "@scalar/json-magic/bundle/plugins/browser";
import { apply, diff, merge } from "@scalar/json-magic/diff";
import { createMagicProxy, getRaw } from "@scalar/json-magic/magic-proxy";
import { upgrade } from "@scalar/openapi-parser";
import { Value } from "@scalar/typebox/value";
import { reactive } from "vue";
import YAML from "yaml";
import { applySelectiveUpdates } from "./helpers/apply-selective-updates.js";
import { deepClone } from "./helpers/deep-clone.js";
import { isObject, safeAssign } from "./helpers/general.js";
import { getValueByPath } from "./helpers/json-path-utils.js";
import { mergeObjects } from "./helpers/merge-object.js";
import { createOverridesProxy } from "./helpers/overrides-proxy.js";
import { createNavigation } from "./navigation/index.js";
import { externalValueResolver, loadingStatus, refsEverywhere, restoreOriginalRefs } from "./plugins.js";
import { getServersFromDocument } from "./preprocessing/server.js";
import { extensions } from "./schemas/extensions.js";
import { InMemoryWorkspaceSchema } from "./schemas/inmemory-workspace.js";
import { defaultReferenceConfig } from "./schemas/reference-config/index.js";
import { coerceValue } from "./schemas/typebox-coerce.js";
import {
  OpenAPIDocumentSchema as OpenAPIDocumentSchemaStrict
} from "./schemas/v3.1/strict/openapi-document.js";
const defaultConfig = {
  "x-scalar-reference-config": defaultReferenceConfig
};
async function loadDocument(workspaceDocument) {
  if ("url" in workspaceDocument) {
    return fetchUrls({ fetch: workspaceDocument.fetch }).exec(workspaceDocument.url);
  }
  return {
    ok: true,
    data: workspaceDocument.document
  };
}
const getDocumentSource = (input) => {
  if ("url" in input) {
    return input.url;
  }
  return void 0;
};
const createWorkspaceStore = (workspaceProps) => {
  const originalDocuments = {};
  const intermediateDocuments = {};
  const documentConfigs = {};
  const overrides = {};
  const documentMeta = {};
  const extraDocumentConfigurations = {};
  const workspace = reactive({
    ...workspaceProps?.meta,
    documents: {},
    /**
     * Returns the currently active document from the workspace.
     * The active document is determined by the 'x-scalar-active-document' metadata field,
     * falling back to the first document in the workspace if no active document is specified.
     *
     * @returns The active document or undefined if no document is found
     */
    get activeDocument() {
      const activeDocumentKey = workspace[extensions.workspace.activeDocument] ?? Object.keys(workspace.documents)[0] ?? "";
      return workspace.documents[activeDocumentKey];
    }
  });
  function getActiveDocumentName() {
    return workspace[extensions.workspace.activeDocument] ?? Object.keys(workspace.documents)[0] ?? "";
  }
  function exportDocument(documentName, format) {
    const intermediateDocument = intermediateDocuments[documentName];
    if (!intermediateDocument) {
      return;
    }
    if (format === "json") {
      return JSON.stringify(intermediateDocument);
    }
    return YAML.stringify(intermediateDocument);
  }
  async function saveDocument(documentName) {
    const intermediateDocument = intermediateDocuments[documentName];
    const workspaceDocument = workspace.documents[documentName];
    if (!workspaceDocument) {
      return;
    }
    const updatedDocument = getRaw(workspaceDocument);
    if (!intermediateDocument || !updatedDocument) {
      return;
    }
    const updatedWithOriginalRefs = await bundle(deepClone(updatedDocument), {
      plugins: [restoreOriginalRefs()],
      treeShake: false,
      urlMap: true
    });
    const excludedDiffs = applySelectiveUpdates(intermediateDocument, updatedWithOriginalRefs);
    return excludedDiffs;
  }
  const processDocument = (input, options) => {
    const servers = getServersFromDocument(options["x-scalar-reference-config"]?.settings?.servers ?? input.servers, {
      baseServerUrl: options["x-scalar-reference-config"]?.settings?.baseServerUrl,
      documentUrl: options.documentSource
    });
    if (servers.length) {
      input.servers = servers.map((it) => ({ url: it.url, description: it.description, variables: it.variables }));
    }
    return input;
  };
  async function addInMemoryDocument(input) {
    const { name, meta } = input;
    const cloned = measureSync("deepClone", () => deepClone(input.document));
    const inputDocument = measureSync("upgrade", () => upgrade(cloned).specification);
    measureSync("initialize", () => {
      if (input.initialize !== false) {
        originalDocuments[name] = deepClone({ ...inputDocument });
        intermediateDocuments[name] = deepClone({ ...inputDocument });
        documentConfigs[name] = input.config ?? {};
        overrides[name] = input.overrides ?? {};
        documentMeta[name] = { documentSource: input.documentSource };
        extraDocumentConfigurations[name] = { fetch: input.fetch };
      }
    });
    const strictDocument = createMagicProxy({ ...inputDocument, ...meta }, { showInternal: true });
    strictDocument["x-original-oas-version"] = input.document.openapi ?? input.document.swagger;
    if (strictDocument[extensions.document.navigation] === void 0) {
      await measureAsync(
        "bundle",
        async () => await bundle(getRaw(strictDocument), {
          treeShake: false,
          plugins: [
            fetchUrls({
              fetch: extraDocumentConfigurations[name]?.fetch ?? workspaceProps?.fetch
            }),
            externalValueResolver(),
            refsEverywhere()
          ],
          urlMap: true,
          origin: documentMeta[name]?.documentSource
          // use the document origin (if provided) as the base URL for resolution
        })
      );
      const coerced = measureSync(
        "coerceValue",
        () => coerceValue(OpenAPIDocumentSchemaStrict, deepClone(strictDocument))
      );
      measureAsync("mergeObjects", async () => mergeObjects(strictDocument, coerced));
    }
    const isValid = Value.Check(OpenAPIDocumentSchemaStrict, strictDocument);
    if (!isValid) {
      const validationErrors = Array.from(Value.Errors(OpenAPIDocumentSchemaStrict, strictDocument));
      console.warn("document validation errors: ");
      console.warn(
        validationErrors.map((error) => ({
          message: error.message,
          path: error.path,
          schema: error.schema,
          value: error.value
        }))
      );
    }
    if (strictDocument[extensions.document.navigation] === void 0) {
      const showModels = input.config?.["x-scalar-reference-config"]?.features?.showModels;
      strictDocument[extensions.document.navigation] = createNavigation(strictDocument, {
        ...input.config?.["x-scalar-reference-config"] ?? {},
        hideModels: showModels === void 0 ? void 0 : !showModels
      }).entries;
      processDocument(getRaw(strictDocument), {
        ...input.config,
        documentSource: input.documentSource
      });
    }
    workspace.documents[name] = createOverridesProxy(
      createMagicProxy(getRaw(strictDocument)),
      input.overrides
    );
  }
  async function addDocument(input) {
    const { name, meta } = input;
    const resolve = await measureAsync(
      "loadDocument",
      async () => await loadDocument({ ...input, fetch: input.fetch ?? workspaceProps?.fetch })
    );
    await measureAsync("addDocument", async () => {
      if (!resolve.ok) {
        console.error(`Failed to fetch document '${name}': request was not successful`);
        workspace.documents[name] = {
          ...meta,
          openapi: "3.1.0",
          info: {
            title: `Document '${name}' could not be loaded`,
            version: "unknown"
          }
        };
        return;
      }
      if (!isObject(resolve.data)) {
        console.error(`Failed to load document '${name}': response data is not a valid object`);
        workspace.documents[name] = {
          ...meta,
          openapi: "3.1.0",
          info: {
            title: `Document '${name}' could not be loaded`,
            version: "unknown"
          }
        };
        return;
      }
      await addInMemoryDocument({
        ...input,
        document: resolve.data,
        documentSource: getDocumentSource(input)
      });
    });
  }
  const getDocumentConfiguration = (name) => {
    return mergeObjects(
      mergeObjects(defaultConfig, workspaceProps?.config ?? {}),
      documentConfigs[name] ?? {}
    );
  };
  const visitedNodesCache = /* @__PURE__ */ new Set();
  return {
    get workspace() {
      return workspace;
    },
    update(key, value) {
      if (key === "__proto__" || key === "constructor" || key === "prototype") {
        throw new Error("Invalid key: cannot modify prototype");
      }
      Object.assign(workspace, { [key]: value });
    },
    updateDocument(name, key, value) {
      const currentDocument = workspace.documents[name === "active" ? getActiveDocumentName() : name];
      if (!currentDocument) {
        throw "Please select a valid document";
      }
      Object.assign(currentDocument, { [key]: value });
    },
    async replaceDocument(documentName, input) {
      const currentDocument = workspace.documents[documentName];
      if (!currentDocument) {
        return console.error(`Document '${documentName}' does not exist in the workspace.`);
      }
      await addInMemoryDocument({
        name: documentName,
        document: input,
        // Preserve the current metadata
        meta: {
          "x-scalar-active-auth": currentDocument["x-scalar-active-auth"],
          "x-scalar-active-server": currentDocument["x-scalar-active-server"]
        },
        initialize: false
      });
    },
    resolve: async (path) => {
      const activeDocument = workspace.activeDocument;
      const target = getValueByPath(activeDocument, path);
      if (!isObject(target)) {
        console.error(
          `Invalid path provided for resolution. Path: [${path.join(", ")}]. Found value of type: ${typeof target}. Expected an object.`
        );
        return;
      }
      return bundle(target, {
        root: activeDocument,
        treeShake: false,
        plugins: [fetchUrls(), loadingStatus(), externalValueResolver()],
        urlMap: true,
        visitedNodes: visitedNodesCache
      });
    },
    addDocument,
    get config() {
      return getDocumentConfiguration(getActiveDocumentName());
    },
    exportDocument,
    exportActiveDocument: (format) => exportDocument(getActiveDocumentName(), format),
    saveDocument,
    async revertDocumentChanges(documentName) {
      const workspaceDocument = workspace.documents[documentName];
      const intermediate = intermediateDocuments[documentName];
      if (!workspaceDocument || !intermediate) {
        return;
      }
      await addInMemoryDocument({
        name: documentName,
        document: intermediate,
        initialize: false
      });
    },
    commitDocument(documentName) {
      console.warn(`Commit operation for document '${documentName}' is not implemented yet.`);
    },
    exportWorkspace() {
      return JSON.stringify({
        documents: {
          ...Object.fromEntries(
            Object.entries(workspace.documents).map(([name, doc]) => [
              name,
              // Extract the raw document data for export, removing any Vue reactivity wrappers.
              // When importing, the document can be wrapped again in a magic proxy.
              getRaw(doc)
            ])
          )
        },
        meta: workspaceProps?.meta ?? {},
        documentConfigs,
        originalDocuments,
        intermediateDocuments,
        overrides,
        documentMeta
      });
    },
    loadWorkspace(input) {
      const result = coerceValue(InMemoryWorkspaceSchema, JSON.parse(input));
      safeAssign(
        workspace.documents,
        Object.fromEntries(
          Object.entries(result.documents).map(([name, doc]) => [
            name,
            createOverridesProxy(createMagicProxy(doc), result.overrides[name])
          ])
        )
      );
      safeAssign(originalDocuments, result.originalDocuments);
      safeAssign(intermediateDocuments, result.intermediateDocuments);
      safeAssign(documentConfigs, result.documentConfigs);
      safeAssign(overrides, result.overrides);
      safeAssign(workspace, result.meta);
      safeAssign(documentMeta, result.documentMeta);
    },
    importWorkspaceFromSpecification: (specification) => {
      const { documents, overrides: overrides2, info: _info, workspace: _workspaceVersion, ...meta } = specification;
      safeAssign(workspace, meta);
      return Promise.all(
        Object.entries(documents ?? {}).map(
          ([name, doc]) => addDocument({ url: doc.$ref, name, overrides: overrides2?.[name] })
        )
      );
    },
    rebaseDocument: (documentName, newDocumentOrigin, resolvedConflicts) => {
      const newOrigin = upgrade(newDocumentOrigin).specification;
      const originalDocument = originalDocuments[documentName];
      const intermediateDocument = intermediateDocuments[documentName];
      const activeDocument = workspace.documents[documentName] ? getRaw(workspace.documents[documentName]) : void 0;
      if (!originalDocument || !intermediateDocument || !activeDocument) {
        return console.error("[ERROR]: Specified document is missing or internal corrupted workspace state");
      }
      const changelogAA = diff(originalDocument, newOrigin);
      const changelogAB = diff(originalDocument, intermediateDocument);
      const changesA = merge(changelogAA, changelogAB);
      if (resolvedConflicts === void 0) {
        return changesA.conflicts;
      }
      const changesetA = changesA.diffs.concat(resolvedConflicts);
      const newIntermediateDocument = apply(deepClone(originalDocument), changesetA);
      intermediateDocuments[documentName] = newIntermediateDocument;
      originalDocuments[documentName] = newOrigin;
      const changelogBA = diff(intermediateDocument, newIntermediateDocument);
      const changelogBB = diff(intermediateDocument, activeDocument);
      const changesB = merge(changelogBA, changelogBB);
      const changesetB = changesB.diffs.concat(changesB.conflicts.flatMap((it) => it[0]));
      const newActiveDocument = coerceValue(
        OpenAPIDocumentSchemaStrict,
        apply(deepClone(newIntermediateDocument), changesetB)
      );
      workspace.documents[documentName] = createOverridesProxy(
        createMagicProxy({ ...newActiveDocument }),
        overrides[documentName]
      );
      return;
    }
  };
};
import { generateClientMutators } from "./mutators/index.js";
export {
  createWorkspaceStore,
  generateClientMutators
};
//# sourceMappingURL=client.js.map
